"use client";
import { submitContactForm } from "@/app/actions";
import { Button } from "@/components/ui/button";
import { CustomInput } from "@/components/ui/custom/custom-input";
import GradientButton from "@/components/ui/custom/gradient-button";
import { SearchableSelect } from "@/components/ui/custom/searchable-select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Toaster } from "@/components/ui/sonner";
import { Textarea } from "@/components/ui/textarea";
import { Collaborte_form_Trusted_Businesses } from "@/lib/dto/contact-us/collaborate-form/collaborate-form-data";
import { zodResolver } from "@hookform/resolvers/zod";
import { CheckCircle2 } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

const formSchema = z.object({
  full_name: z.string().min(1, "Full name is required"),
  email: z.string().email("Valid email is required"),
  phone: z.string().min(1, "Phone number is required"),
  company_name: z.string().optional(),
  want_to_build: z.string().min(1, "Project type is required"),
  project_idea: z.string().optional(),
  budget_range: z.string().min(1, "Budget range is required"),
  timeline: z.string().min(1, "Timeline is required"),
  how_did_you_hear_about_us: z
    .string()
    .min(1, "How did you hear about us is required"),
});

type FormValues = z.infer<typeof formSchema>;

export default function CollaborateForm() {
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    setValue,
    watch,
    reset,
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
  });


  const onSubmit = async (data: FormValues) => {
    setIsLoading(true);

    try {
      // Call server action instead of direct API call
      const result = await submitContactForm({
        full_name: data.full_name,
        email: data.email,
        phone: data.phone || "",
        company_name: data.company_name || "",
        want_to_build: data.want_to_build || "",
        project_idea: data.project_idea || "",
        budget_range: data.budget_range || "",
        timeline: data.timeline || "",
        how_did_you_hear_about_us: data.how_did_you_hear_about_us || "",
      });

      if (result.success) {
        setShowSuccessDialog(true);
        reset({
          full_name: "",
          email: "",
          phone: "",
          company_name: "",
          want_to_build: "",
          project_idea: "",
          budget_range: "",
          timeline: "",
          how_did_you_hear_about_us: "",
        });
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      console.error("Server Action Error:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const watchedValues = watch();

  return (
    <>
      <Toaster />
      <Dialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <DialogContent className="gap-0 border-none p-8 sm:max-w-md sm:p-10">
          <DialogHeader className="items-center gap-0">
            <div className="mx-auto mb-5 flex h-[72px] w-[72px] items-center justify-center rounded-full bg-[#5231E7]/10">
              <CheckCircle2
                className="h-9 w-9 text-[#5231E7]"
                aria-hidden="true"
              />
            </div>
            <DialogTitle className="text-center text-2xl font-semibold text-custom-navy-blue">
              {Collaborte_form_Trusted_Businesses.successDialog.title}
            </DialogTitle>
            <DialogDescription asChild>
              <div className="mt-4 space-y-3 text-center text-base leading-relaxed text-[#6B6B6B]">
                <p>{Collaborte_form_Trusted_Businesses.successDialog.description}</p>
                <p>{Collaborte_form_Trusted_Businesses.successDialog.followUp}</p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <div className="mt-8">
            <Button
              type="button"
              className="h-12 w-full rounded-full bg-[#5231E7] text-base font-medium hover:bg-[#764AF1]"
              onClick={() => setShowSuccessDialog(false)}
            >
              {Collaborte_form_Trusted_Businesses.successDialog.closeText}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <form onSubmit={handleSubmit(onSubmit)} className="mx-auto space-y-6">
        {/* Section 1 */}
        <div className="space-y-3.75">
          <p className="text-base font-normal text-custom-navy-blue">
            {Collaborte_form_Trusted_Businesses.section1.title}
          </p>
          <div className="grid grid-cols-2 gap-x-5.5 gap-y-4.75 ">
            <div>
              <CustomInput
                variant="custom"
                placeholder={
                  Collaborte_form_Trusted_Businesses.section1.fields.name
                }
                {...register("full_name")}
              />
              {errors.full_name && (
                <p className="text-red-500 text-sm">
                  {errors.full_name.message}
                </p>
              )}
            </div>
            <div>
              <CustomInput
                variant="custom"
                placeholder={
                  Collaborte_form_Trusted_Businesses.section1.fields.email
                }
                {...register("email")}
              />
              {errors.email && (
                <p className="text-red-500 text-sm">{errors.email.message}</p>
              )}
            </div>
            <div>
              <CustomInput
                variant="custom"
                placeholder={
                  Collaborte_form_Trusted_Businesses.section1.fields.phone
                }
                {...register("phone")}
              />
              {errors.phone && (
                <p className="text-red-500 text-sm">{errors.phone.message}</p>
              )}
            </div>
            <div>
              <CustomInput
                variant={"custom"}
                placeholder={
                  Collaborte_form_Trusted_Businesses.section1.fields.company
                }
                {...register("company_name")}
                className="px-5 md:px-6"
              />
            </div>
          </div>
        </div>

        {/* Section 2 */}
        <div className="space-y-3.75">
          <p className="text-base font-normal text-custom-navy-blue">
            {Collaborte_form_Trusted_Businesses.section2.title}
          </p>
          <div className="space-y-4.75">
            <div className="">
              <SearchableSelect
                options={
                  Collaborte_form_Trusted_Businesses.section2.fields.projectType
                    .options
                }
                placeholder={
                  Collaborte_form_Trusted_Businesses.section2.fields.projectType
                    .placeholder
                }
                value={watchedValues.want_to_build || ""}
                onValueChange={(val) => setValue("want_to_build", val)}
              />
              {errors.want_to_build && (
                <p className="text-red-500 text-sm">
                  {errors.want_to_build.message}
                </p>
              )}
            </div>

            <Textarea
              className="h-31 w-full rounded-[10px] px-3 md:px-6 py-5 md:text-base text-custom-navy-blue font-semibold placeholder:text-base placeholder:font-semibold focus-visible:border-ring focus-visible:ring-primary focus-visible:ring-[2px] focus-visible:ring-offset-0"
              placeholder={
                Collaborte_form_Trusted_Businesses.section2.fields.idea
              }
              {...register("project_idea")}
            />
            {errors.project_idea && (
              <p className="text-red-500 text-sm">
                {errors.project_idea.message}
              </p>
            )}
            <div>
              <Select
                value={watchedValues.budget_range || ""}
                onValueChange={(val) => setValue("budget_range", val)}
              >
                <SelectTrigger className="w-full rounded-[10px] xl:h-14.25 lg:h-12 px-3 md:px-6 py-7 md:text-base text-custom-navy-blue font-semibold placeholder:text-base placeholder:font-semibold focus-visible:border-ring focus-visible:ring-primary focus-visible:ring-[2px] focus-visible:ring-offset-0">
                  <SelectValue
                    placeholder={
                      Collaborte_form_Trusted_Businesses.section2.fields.budget
                        .placeholder
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {Collaborte_form_Trusted_Businesses.section2.fields.budget.options.map(
                    (v, i) => (
                      <SelectItem key={i} value={v.value}>
                        {v.label}
                      </SelectItem>
                    )
                  )}
                </SelectContent>
              </Select>
              {errors.budget_range && (
                <p className="text-red-500 text-sm">
                  {errors.budget_range.message}
                </p>
              )}
            </div>

            <div>
              <Select
                value={watchedValues.timeline || ""}
                onValueChange={(val) => setValue("timeline", val)}
              >
                <SelectTrigger className="w-full rounded-[10px] xl:h-14.25 lg:h-12 px-3 md:px-6 py-7 md:text-base text-custom-navy-blue font-semibold placeholder:text-base placeholder:font-semibold focus-visible:border-ring focus-visible:ring-primary focus-visible:ring-[2px] focus-visible:ring-offset-0">
                  <SelectValue
                    placeholder={
                      Collaborte_form_Trusted_Businesses.section2.fields
                        .timeline.placeholder
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {Collaborte_form_Trusted_Businesses.section2.fields.timeline.options.map(
                    (v, i) => (
                      <SelectItem key={i} value={v.value}>
                        {v.label}
                      </SelectItem>
                    )
                  )}
                </SelectContent>
              </Select>
              {errors.timeline && (
                <p className="text-red-500 text-sm">
                  {errors.timeline.message}
                </p>
              )}
            </div>

            <div>
              <Select
                value={watchedValues.how_did_you_hear_about_us || ""}
                onValueChange={(val) =>
                  setValue("how_did_you_hear_about_us", val)
                }
              >
                <SelectTrigger className="w-full rounded-[10px] xl:h-14.25 lg:h-12 px-3 md:px-6 py-7 md:text-base text-custom-navy-blue font-semibold placeholder:text-base placeholder:font-semibold focus-visible:border-ring focus-visible:ring-primary focus-visible:ring-[2px] focus-visible:ring-offset-0">
                  <SelectValue
                    placeholder={
                      Collaborte_form_Trusted_Businesses.section2.fields
                        .referral.placeholder
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {Collaborte_form_Trusted_Businesses.section2.fields.referral.options.map(
                    (v, i) => (
                      <SelectItem key={i} value={v.value}>
                        {v.label}
                      </SelectItem>
                    )
                  )}
                </SelectContent>
              </Select>
              {errors.how_did_you_hear_about_us && (
                <p className="text-red-500 text-sm">
                  {errors.how_did_you_hear_about_us.message}
                </p>
              )}
            </div>
          </div>
        </div>


        {/* Submit Button */}
        <div>
          <GradientButton type="submit" disabled={isLoading}>
            {isLoading ? (
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Submitting...
              </div>
            ) : (
              <>{Collaborte_form_Trusted_Businesses.submitText}</>
            )}
          </GradientButton>
          <p className="text-sm md:text-base text-[#B6B6B6] mt-7">
            {
              Collaborte_form_Trusted_Businesses.privacyText.split(
                "Submit Request"
              )[0]
            }{" "}
            <strong>Submit Request</strong>
            {
              Collaborte_form_Trusted_Businesses.privacyText.split(
                "Submit Request"
              )[1]
            }{" "}
            <a href="/privacy-policy" className="underline">
              Privacy Policy
            </a>
          </p>
        </div>
      </form>
    </>
  );
}
